// hooks/useAuth.ts
// Hook React per gestione autenticazione

'use client';

import { useState, useEffect, useCallback } from 'react';
import { User } from '@supabase/supabase-js';
import {
  getSupabaseClient,
  getCurrentUser,
  getUserProfile,
  getUserLimits,
  signIn as authSignIn,
  signUp as authSignUp,
  signOut as authSignOut,
  type UserProfile,
  type UserLimits,
  type SignInData,
  type SignUpData,
} from '@/lib/supabase/auth';

interface UseAuthReturn {
  user: User | null;
  profile: UserProfile | null;
  limits: UserLimits | null;
  loading: boolean;
  signIn: (data: SignInData) => Promise<void>;
  signUp: (data: SignUpData) => Promise<void>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  refreshLimits: () => Promise<void>;
  isAuthenticated: boolean;
  isFree: boolean;
  isPremium: boolean;
  isAdmin: boolean;
}

export function useAuth(): UseAuthReturn {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [limits, setLimits] = useState<UserLimits | null>(null);
  const [loading, setLoading] = useState(true);

  const loadUser = useCallback(async () => {
    try {
      // getSession() reads from cookies — no network, always fast.
      // setLoading(false) happens here, not after profile/limits fetches.
      const currentUser = await getCurrentUser();
      setUser(currentUser);
      setLoading(false);

      if (currentUser) {
        // Load profile/limits async without blocking loading state.
        // If network is slow or offline these fail silently — UI still works.
        Promise.all([getUserProfile(), getUserLimits()])
          .then(([p, l]) => { setProfile(p); setLimits(l); })
          .catch(() => {});
      } else {
        setProfile(null);
        setLimits(null);
      }
    } catch (error) {
      console.error('Error loading user:', error);
      setUser(null);
      setProfile(null);
      setLimits(null);
      setLoading(false);
    }
  }, []);

  const refreshProfile = useCallback(async () => {
    if (!user) return;
    try {
      const userProfile = await getUserProfile();
      setProfile(userProfile);
    } catch (error) {
      console.error('Error refreshing profile:', error);
    }
  }, [user]);

  const refreshLimits = useCallback(async () => {
    if (!user) return;
    try {
      const userLimits = await getUserLimits();
      setLimits(userLimits);
    } catch (error) {
      console.error('Error refreshing limits:', error);
    }
  }, [user]);

  const signIn = useCallback(async (data: SignInData) => {
    setLoading(true);
    try {
      await authSignIn(data);
      await loadUser();
    } catch (error) {
      setLoading(false);
      throw error;
    }
  }, [loadUser]);

  const signUp = useCallback(async (data: SignUpData) => {
    setLoading(true);
    try {
      await authSignUp(data);
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const signOut = useCallback(async () => {
    try {
      await authSignOut();
      setUser(null);
      setProfile(null);
      setLimits(null);
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  }, []);

  useEffect(() => {
    const supabase = getSupabaseClient();
    loadUser();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log('[auth event]', event, session?.user?.id);
        // Sync user directly from the event session — no extra network calls.
        // loadUser() already ran on mount for the initial load.
        if (event === 'SIGNED_OUT') {
          setUser(null);
          setProfile(null);
          setLimits(null);
          setLoading(false);
        } else if (session?.user) {
          setUser(prev => prev?.id === session.user.id ? prev : session.user);
          setLoading(false);
        }
      }
    );

    return () => { subscription.unsubscribe(); };
  }, [loadUser]);

  return {
    user,
    profile,
    limits,
    loading,
    signIn,
    signUp,
    signOut,
    refreshProfile,
    refreshLimits,
    isAuthenticated: !!user,
    isFree: profile?.role === 'free',
    isPremium: profile?.role === 'premium',
    isAdmin: profile?.role === 'admin',
  };
}